/**
 * Das Markup des öffentlichen Legal-Footers (Regel #21) — ohne Hook, ohne
 * Router, ohne Übersetzungs-Kontext.
 *
 * Zwei Leser: `PublicFooter` (jede öffentliche Seite, mit `useTranslation`
 * und React-Router-Links) und die Fehlerseiten von Next.js
 * (`components/fehlerseite/`), die gerade dann laufen, wenn weder das Backend
 * noch das Bündel der App da ist — dort gibt es weder Router noch Wörterbuch.
 * Eine zweite Fassung des Footers für die Fehlerseiten wäre die Drift, vor
 * der `utils/legal.ts` warnt: Inhalt und Aussehen stehen deshalb hier, die
 * Leser liefern nur Übersetzer und Link.
 */
import { Fragment, type ReactNode } from 'react';

import { FOOTER_COPYRIGHT, FOOTER_LINKS } from '../utils/legal';

export type FooterLink = (typeof FOOTER_LINKS)[number];

/** Übersetzer mit deutscher Vorgabe — derselbe Vertrag wie `t` aus `useTranslation`. */
export type FooterUebersetzer = (schluessel: string, vorgabe: string) => string;

interface Eigenschaften {
  t: FooterUebersetzer;
  /** Ein internes Ziel (`to`) als Link — `<Link>` in der App, `<a href>` auf einer Fehlerseite. */
  intern: (link: FooterLink & { to: string }, text: string) => ReactNode;
  /**
   * Öffnet die Cookie-Einstellungen. Ohne ihn steht kein Knopf da: Die
   * Fehlerseiten haben keinen Einwilligungs-Banner, den er öffnen könnte —
   * und dort läuft auch nichts, worin eingewilligt werden müsste.
   */
  cookieEinstellungen?: () => void;
  /**
   * Die Sprach-Links der Seite (`SprachLinks` über `PublicFooter`). Ohne sie steht keiner da:
   * Eine Fehlerseite kennt ihren Pfad nicht und hat keine Sprachfassungen.
   */
  sprachen?: ReactNode;
  /**
   * Die Zeile „Rechner & Werkzeuge“ (`PublicFooter`): jede Werkzeugseite von
   * jeder öffentlichen Seite aus verlinkt. Ohne sie steht keine da — die
   * Fehlerseiten kennen weder Sprache noch Router.
   */
  werkzeuge?: ReactNode;
}

export default function LegalFooterInhalt({ t, intern, cookieEinstellungen, sprachen, werkzeuge }: Eigenschaften) {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-2xl leading-relaxed text-[color:var(--marke-121apps)] mb-4">
          {t('footer.disclaimer',
            'Paperball ist ein mathematisches Backtest- und Analysewerkzeug und erbringt keine ' +
            'Anlageberatung, Anlagevermittlung oder Finanzportfolioverwaltung im Sinne des WpIG/WpHG. ' +
            'Es werden keine Kauf-, Verkaufs- oder Halteempfehlungen gegeben. Alle Anlageentscheidungen ' +
            'triffst du eigenverantwortlich. Backtests rechnen mit historischen Kursen zurück, ' +
            'Monte-Carlo-Simulationen setzen sie zu möglichen Verläufen neu zusammen. Beides ist ' +
            'keine Vorhersage, und vergangene Wertentwicklungen sind kein verlässlicher Hinweis auf künftige.'
          )}
        </p>
        {werkzeuge}
        {sprachen}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-base text-slate-600">
          <div>{FOOTER_COPYRIGHT}</div>
          {/* `flex-wrap`: sechs Rechts-Links passen auf 375 px in keiner
              Sprache in eine Zeile — auf Franzoesisch messen sie 424 px und
              haben die ganze Seite waagerecht scrollen lassen. Ohne Umbruch
              schiebt jede laengere Uebersetzung „Kontakt" aus dem Bild, und
              Regel #21 verlangt, dass Impressum und Datenschutz von JEDER
              Seite aus erreichbar sind — auch vom Telefon. */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 [&>*]:whitespace-nowrap">
            {FOOTER_LINKS.map((l) => {
              const text = t(l.key, l.fallback);
              return l.to !== undefined
                ? <Fragment key={l.key}>{intern({ ...l, to: l.to }, text)}</Fragment>
                : <a key={l.key} href={l.href} className="hover:text-gray-700">{text}</a>;
            })}
            {/* Widerrufen so einfach wie Einwilligen (Art. 7 Abs. 3 DSGVO). */}
            {cookieEinstellungen && (
              <button type="button" className="footer-knopf hover:text-gray-700" onClick={cookieEinstellungen}>
                {t('consent.settings_link', 'Cookie-Einstellungen')}
              </button>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
